import { useEffect, useRef, useState } from "react";

import { useLocalization } from "@fluent/react";
import { useQueryClient } from "@tanstack/react-query";
import { useNotificationContext } from "cerulean";

import { transactionDetailQueryOptions } from "../../liveLedger/hooks/useTransactionDetail";
import { TRANSACTION_ID_PARAM } from "../../liveLedger/constants";

// Fixed so every click reuses the same browser tab instead of piling up new ones.
export const VIEW_TRANSACTION_TAB_NAME = "narmi-live-ledger-transaction";

// Module-level: the reserved window is one shared resource across every hook instance, not per-instance.
let latestController: AbortController | null = null;

interface UseOpenTransactionInLedgerParams {
  activeReceiptId: string;
}

export const useOpenTransactionInLedger = ({
  activeReceiptId,
}: UseOpenTransactionInLedgerParams) => {
  const { l10n } = useLocalization();
  const queryClient = useQueryClient();
  const { sendNotification } = useNotificationContext();
  const [isPending, setIsPending] = useState(false);
  const instanceControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setIsPending(false);
    return () => {
      instanceControllerRef.current?.abort();
    };
  }, [activeReceiptId]);

  const openTransaction = async (transactionId: string) => {
    // Reserved synchronously in the click's user-activation window, so navigating it later isn't treated as a popup.
    const reservedWindow = window.open("", VIEW_TRANSACTION_TAB_NAME);
    if (!reservedWindow) {
      sendNotification({
        type: "negative",
        text: l10n.getString("receipts-drawer-view-transaction-error"),
      });
      return;
    }
    // Claimed only now, after confirming we have a window — a blocked click must not steal ownership from one that succeeded.
    instanceControllerRef.current?.abort();
    const controller = new AbortController();
    instanceControllerRef.current = controller;
    latestController?.abort();
    latestController = controller;
    const ownsLatestReservation = () => latestController === controller;
    // Severs opener so a cross-origin page in that tab can't reach back and redirect this one.
    try {
      reservedWindow.opener = null;
    } catch {
      // Already inaccessible cross-origin; nothing to sever.
    }
    // Read fresh at each close, not snapshotted: the user could navigate this tab themselves while the fetch is in flight.
    const isStillBlank = () => {
      try {
        return (
          reservedWindow.location.href === "about:blank" ||
          reservedWindow.location.href === ""
        );
      } catch {
        return false;
      }
    };
    setIsPending(true);
    try {
      // staleTime: 0 forces a real request to confirm the transaction is still there.
      await queryClient.fetchQuery({
        ...transactionDetailQueryOptions(transactionId),
        staleTime: 0,
      });
      // A superseded request touches nothing — only the reservation's owner may close or navigate it.
      if (!ownsLatestReservation()) return;
      if (controller.signal.aborted) {
        if (isStillBlank()) reservedWindow.close();
        return;
      }
      // Absolute, from this page's origin — a relative path resolves against the reused tab's current origin instead.
      reservedWindow.location.href = `${window.location.origin}/live-ledger?${TRANSACTION_ID_PARAM}=${transactionId}`;
    } catch {
      if (ownsLatestReservation() && isStillBlank()) reservedWindow.close();
      if (!ownsLatestReservation() || controller.signal.aborted) {
        return;
      }
      sendNotification({
        type: "negative",
        text: l10n.getString("receipts-drawer-view-transaction-error"),
      });
    } finally {
      if (instanceControllerRef.current === controller) {
        setIsPending(false);
      }
    }
  };

  return { openTransaction, isPending };
};
